import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmation } from './ConfirmationContext';
import { useAlert } from './AlertContext';
import Table from './Tables';
import Button from './Buttons';
import { formatDateUTC } from '../utils/formatDate';
import { useChannelApi } from '../Services/ChannelApiProvider';
import TriggerSettings from './TriggerSettings';
import SearchInput from './SearchInput';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

function TriggersList({ channelId, canEdit, loading }) {
  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();
  const { getTriggers, deleteTrigger, getSearchTriggers } = useChannelApi();

  const [editingTrigger, setEditingTrigger] = useState(null);
  const [channelTriggers, setChannelTriggers] = useState([]);
  const [totalTriggers, setTotalTriggers] = useState(0);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [currentAppliedSearchTerm, setCurrentAppliedSearchTerm] = useState('');

  const tableTitle = 'Triggers';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const updateTriggerList = () =>
    fetchTriggers(persistedSkip, persistedPageSize, currentAppliedSearchTerm);

  const fetchTriggers = useCallback(
    async (skip, limit, search_query = '') => {
      if (channelId && channelId !== 'new-channel') {
        setTriggersLoading(true);
        try {
          let responseData;
          if (search_query) {
            responseData = await getSearchTriggers(channelId, search_query, skip, limit);
          } else {
            responseData = await getTriggers(channelId, skip, limit);
          }
          setChannelTriggers(responseData.items || []);
          setTotalTriggers(responseData.totalCount || 0);
        } catch (err) {
          console.error('Failed to fetch triggers:', err);
          if (err.response && err.response.status === 404) {
            setChannelTriggers([]);
            setTotalTriggers(0);
          }
        } finally {
          setTriggersLoading(false);
        }
      }
    },
    [channelId, getTriggers, getSearchTriggers],
  );

  useEffect(() => {
    setCurrentAppliedSearchTerm(debouncedSearchTerm);
    fetchTriggers(persistedSkip, persistedPageSize, debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchTriggers, persistedSkip, persistedPageSize]);

  const handleTableChange = useCallback(
    (skip, limit) => {
      fetchTriggers(skip, limit, currentAppliedSearchTerm);
    },
    [fetchTriggers, currentAppliedSearchTerm],
  );

  useEffect(() => {
    if (channelId && channelId !== 'new-channel' && !searchTerm) {
      fetchTriggers(persistedSkip, persistedPageSize, '');
    }
  }, [channelId, fetchTriggers, persistedSkip, persistedPageSize, searchTerm]);

  const typeMap = {
    start: 'Start',
    thematic: 'Thematic',
    no_thematic: 'No Thematic',
    phrase: 'Phrase',
    system: 'System',
  };

  const actionMap = {
    moderate: 'Moderate',
    ignore: 'Ignore',
    skip: 'Skip',
    deactivate: 'Deactivate',
    deactivate_forever: 'Deactivate Forever',
    close: 'Close',
    forward: 'Forward',
    reply_with: 'Reply With',
  };

  const columns = [
    {
      name: 'Type',
      selector: (row) => typeMap[row.trigger_type] || row.trigger_type,
      sortable: false,
    },
    { name: 'Action', selector: (row) => actionMap[row.action] || row.action, sortable: false },
    {
      name: 'On',
      selector: (row) => {
        const trigger_message = row.trigger_message ?? '';
        const limit = 60;

        return trigger_message.length > limit
          ? `${trigger_message.substring(0, limit)}...`
          : trigger_message;
      },
      sortable: false,
    },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: false },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
  ];

  const handleEditClick = (row) => {
    setEditingTrigger(row);
  };

  const handleCreateClick = () => {
    setEditingTrigger({
      trigger_message: '',
      action: '',
      thematic_id: '',
      is_active: true,
      trigger_type: '',
      reply_with: '',
      confidence_level: 50,
      notification_id: '',
    });
  };

  const handleDelete = (selectedId) => {
    if (!selectedId || !canEdit) return;
    showConfirmation('Are you sure you want to delete this trigger?', async () => {
      try {
        await deleteTrigger(channelId, selectedId);
        updateTriggerList();
      } catch (error) {
        showAlert('fail', 'Failed to delete the trigger.');
      }
    });
  };

  return (
    <div className="radius-yes p-5 bg-yes col-12">
      {editingTrigger ? (
        <TriggerSettings
          channelId={channelId}
          initialData={editingTrigger}
          canEdit={canEdit}
          onClose={() => setEditingTrigger(null)}
          onUpdate={() => {
            setEditingTrigger(null);
            updateTriggerList();
          }}
          onUpdateTrigger={updateTriggerList}
        />
      ) : (
        <>
          <h2 className="mb-4">
            <SearchInput
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search triggers..."
            />
          </h2>
          <Table
            title={tableTitle}
            columns={columns}
            data={channelTriggers}
            onRowClicked={handleEditClick}
            onDelete={handleDelete}
            selectableRows={canEdit}
            selectableRowsHighlight
            pagination
            paginationServer
            progressPending={loading}
            paginationTotalRows={totalTriggers}
            paginationPerPage={persistedPageSize}
            onChangePage={handleTableChange}
          />
          {canEdit && (
            <div className="d-flex justify-start" style={{ marginTop: '1rem' }}>
              <Button size="large" onClick={handleCreateClick}>
                Create New Trigger
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TriggersList;
